import { toHast } from '@googleworkspace/google-docs-hast';
import { sanitize, defaultSchema } from 'hast-util-sanitize';
import type { Schema } from 'hast-util-sanitize';
import { toHtml } from 'hast-util-to-html';
import type { Content, Element, Root } from 'hast';

type GoogleDocForHast = Parameters<typeof toHast>[0];

/**
 * Enable verbose logs for `googleDocJsonToSanitizedHtml`:
 * - in development, **on** unless `localStorage.DEBUG_GOOGLE_DOCS_HTML === '0'`
 * - in production, set `localStorage.DEBUG_GOOGLE_DOCS_HTML = '1'` to opt in
 */
function isGoogleDocsHtmlDebugEnabled(): boolean {
    if (typeof localStorage === 'undefined') {
        return process.env.NODE_ENV === 'development';
    }
    const flag = localStorage.getItem('DEBUG_GOOGLE_DOCS_HTML');
    if (flag === '0') return false;
    if (flag === '1') return true;
    return process.env.NODE_ENV === 'development';
}

function debugGoogleDocsHtml(...args: unknown[]): void {
    if (!isGoogleDocsHtmlDebugEnabled()) return;
    console.debug('[googleDocsHtml]', ...args);
}

function countImgNodes(node: Root): number {
    let n = 0;
    const visit = (x: Content): void => {
        if (x.type !== 'element') return;
        if (x.tagName === 'img') n++;
        if (x.children) {
            for (const c of x.children) visit(c);
        }
    };
    for (const c of node.children) {
        visit(c);
    }
    return n;
}

function collectImgSrcSamples(node: Root, limit: number): string[] {
    const out: string[] = [];
    const visit = (x: Content): void => {
        if (out.length >= limit) return;
        if (x.type !== 'element') return;
        if (x.tagName === 'img') {
            const src = (x.properties as Record<string, unknown> | undefined)?.src;
            out.push(typeof src === 'string' ? src.slice(0, 160) : `[non-string src: ${typeof src}]`);
        }
        if (x.children) {
            for (const c of x.children) visit(c);
        }
    };
    for (const c of node.children) {
        visit(c);
    }
    return out;
}

/** `inlineObjectElement.inlineObjectId` values referenced from a structural subtree (body, header, footer, etc.). */
function collectReferencedInlineObjectIds(body: unknown): string[] {
    const ids = new Set<string>();
    function walk(v: unknown): void {
        if (!v || typeof v !== 'object') return;
        if (Array.isArray(v)) {
            for (const item of v) walk(item);
            return;
        }
        const o = v as Record<string, unknown>;
        const io = o.inlineObjectElement;
        if (io && typeof io === 'object' && io !== null) {
            const id = (io as Record<string, unknown>).inlineObjectId;
            if (typeof id === 'string') ids.add(id);
        }
        for (const k of Object.keys(o)) walk(o[k]);
    }
    walk(body);
    return [...ids];
}

function summarizeInlineObjectsForDebug(inlineObjects: GoogleDocForHast['inlineObjects'] | undefined): Array<{
    id: string;
    hasEmbeddedObject: boolean;
    hasImageProperties: boolean;
    contentUri?: string;
    sourceUri?: string;
    note?: string;
}> {
    if (!inlineObjects) return [];
    return Object.keys(inlineObjects)
        .slice(0, 12)
        .map(id => {
            const o = inlineObjects[id] as Record<string, unknown> | undefined;
            const emb = o?.inlineObjectProperties as Record<string, unknown> | undefined;
            const embedded = emb?.embeddedObject as Record<string, unknown> | undefined;
            const img = embedded?.imageProperties as Record<string, unknown> | undefined;
            const contentUri = img?.contentUri;
            const sourceUri = img?.sourceUri;
            let note: string | undefined;
            if (img && typeof contentUri !== 'string' && typeof sourceUri === 'string') {
                note = 'imageProperties has sourceUri but no contentUri (google-docs-hast only uses contentUri)';
            }
            return {
                id: id.length > 48 ? `${id.slice(0, 45)}…` : id,
                hasEmbeddedObject: !!embedded,
                hasImageProperties: !!img,
                contentUri: typeof contentUri === 'string' ? contentUri.slice(0, 120) : undefined,
                sourceUri: typeof sourceUri === 'string' ? sourceUri.slice(0, 120) : undefined,
                note,
            };
        });
}

/**
 * Extend GitHub-style defaults:
 * - `style` / `className` on any tag (Google inline styles)
 * - `u` — default schema omits it, but google-docs-hast uses `<u>` for underline
 * - `referrerPolicy` on `img` — helps `*.googleusercontent.com` doc images load when hotlinked
 */
const googleDocsSanitizeSchema: Schema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
    attributes: {
        ...defaultSchema.attributes,
        img: [...(defaultSchema.attributes?.img ?? []), 'referrerPolicy'],
        '*': [...(defaultSchema.attributes?.['*'] ?? []), 'style', 'className'],
    },
};

/** Google-hosted doc image URLs often 403 when embedded; `no-referrer` frequently fixes hotlinking. */
function applyGoogleDocImgReferrerPolicy(tree: Root): void {
    const visit = (node: Content): void => {
        if (node.type === 'element') {
            if (node.tagName === 'img') {
                const props = (node.properties ??= {}) as Record<string, unknown>;
                props.referrerPolicy = 'no-referrer';
            }
            if (node.children) {
                for (const child of node.children) {
                    visit(child);
                }
            }
        }
    };
    for (const child of tree.children) {
        visit(child);
    }
}

type TabLike = {
    tabProperties?: { tabId?: string };
    documentTab?: GoogleDocForHast & {
        body?: GoogleDocForHast['body'];
        lists?: GoogleDocForHast['lists'];
        inlineObjects?: GoogleDocForHast['inlineObjects'];
        positionedObjects?: Record<string, unknown>;
    };
};

/** Tab `documentTab` for the active tab (same selection as {@link normalizeGoogleDocForToHast}), or null for non-tabbed docs. */
function getTabDocumentRecordFromRaw(raw: Record<string, unknown>, tabId: string | null): Record<string, unknown> | null {
    if (raw.tab && typeof raw.tab === 'object') {
        const dt = (raw.tab as TabLike).documentTab;
        return dt ? (dt as Record<string, unknown>) : null;
    }
    const tabs = raw.tabs as TabLike[] | undefined;
    if (tabs && Array.isArray(tabs) && tabs.length > 0) {
        const selected = tabId ? tabs.find(t => t.tabProperties?.tabId === tabId) : tabs[0];
        const dt = selected?.documentTab;
        return dt ? (dt as Record<string, unknown>) : null;
    }
    return null;
}

/** Like {@link mergedInlineObjects}, for floating images (`positionedObjects`). */
function mergedPositionedObjects(
    doc: Record<string, unknown>,
    documentTab: Record<string, unknown> | null | undefined
): Record<string, unknown> {
    const root = (doc.positionedObjects ?? {}) as Record<string, unknown>;
    const tab = (documentTab?.positionedObjects ?? {}) as Record<string, unknown>;
    return { ...root, ...tab };
}

function mergeRecordPreferLater(a: Record<string, unknown> | undefined, b: Record<string, unknown> | undefined): Record<string, unknown> {
    return { ...(a ?? {}), ...(b ?? {}) };
}

/** `headers` map from document root and active tab (tab may hold tab-specific headers). */
function mergedHeadersMap(raw: Record<string, unknown>, tabRef: Record<string, unknown> | null): Record<string, unknown> {
    const root = raw.headers as Record<string, unknown> | undefined;
    const tab = tabRef?.headers as Record<string, unknown> | undefined;
    return mergeRecordPreferLater(root, tab);
}

function mergedFootersMap(raw: Record<string, unknown>, tabRef: Record<string, unknown> | null): Record<string, unknown> {
    const root = raw.footers as Record<string, unknown> | undefined;
    const tab = tabRef?.footers as Record<string, unknown> | undefined;
    return mergeRecordPreferLater(root, tab);
}

function getMergedDocumentStyle(raw: Record<string, unknown>, tabRef: Record<string, unknown> | null): Record<string, unknown> {
    const root = raw.documentStyle as Record<string, unknown> | undefined;
    const tab = tabRef?.documentStyle as Record<string, unknown> | undefined;
    return mergeRecordPreferLater(root, tab);
}

function pushHeaderFooterIdIfNew(ids: string[], seen: Set<string>, id: unknown): void {
    if (typeof id === 'string' && id.length > 0 && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
    }
}

function collectSectionStyleHeaderIdsFromBody(bodyContent: unknown[], ids: string[], seen: Set<string>): void {
    for (const se of bodyContent) {
        if (!se || typeof se !== 'object') continue;
        const sb = (se as Record<string, unknown>).sectionBreak as Record<string, unknown> | undefined;
        const sectionStyle = sb?.sectionStyle as Record<string, unknown> | undefined;
        if (!sectionStyle) continue;
        pushHeaderFooterIdIfNew(ids, seen, sectionStyle.defaultHeaderId);
        pushHeaderFooterIdIfNew(ids, seen, sectionStyle.firstPageHeaderId);
        pushHeaderFooterIdIfNew(ids, seen, sectionStyle.evenPageHeaderId);
    }
}

function collectSectionStyleFooterIdsFromBody(bodyContent: unknown[], ids: string[], seen: Set<string>): void {
    for (const se of bodyContent) {
        if (!se || typeof se !== 'object') continue;
        const sb = (se as Record<string, unknown>).sectionBreak as Record<string, unknown> | undefined;
        const sectionStyle = sb?.sectionStyle as Record<string, unknown> | undefined;
        if (!sectionStyle) continue;
        pushHeaderFooterIdIfNew(ids, seen, sectionStyle.defaultFooterId);
        pushHeaderFooterIdIfNew(ids, seen, sectionStyle.firstPageFooterId);
        pushHeaderFooterIdIfNew(ids, seen, sectionStyle.evenPageFooterId);
    }
}

/**
 * Best-effort order: documentStyle and section breaks first, then any remaining header ids (stable sort).
 * `google-docs-hast` does not render headers; we prepend these blocks before the body.
 */
function collectOrderedHeaderIds(
    raw: Record<string, unknown>,
    tabRef: Record<string, unknown> | null,
    headersMap: Record<string, unknown>,
    bodyContent: unknown[]
): string[] {
    const ids: string[] = [];
    const seen = new Set<string>();
    const docStyle = getMergedDocumentStyle(raw, tabRef);
    pushHeaderFooterIdIfNew(ids, seen, docStyle.firstPageHeaderId);
    pushHeaderFooterIdIfNew(ids, seen, docStyle.evenPageHeaderId);
    pushHeaderFooterIdIfNew(ids, seen, docStyle.defaultHeaderId);
    collectSectionStyleHeaderIdsFromBody(bodyContent, ids, seen);
    const remaining = Object.keys(headersMap)
        .filter(k => !seen.has(k))
        .sort();
    for (const k of remaining) {
        pushHeaderFooterIdIfNew(ids, seen, k);
    }
    return ids;
}

function collectOrderedFooterIds(
    raw: Record<string, unknown>,
    tabRef: Record<string, unknown> | null,
    footersMap: Record<string, unknown>,
    bodyContent: unknown[]
): string[] {
    const ids: string[] = [];
    const seen = new Set<string>();
    const docStyle = getMergedDocumentStyle(raw, tabRef);
    pushHeaderFooterIdIfNew(ids, seen, docStyle.firstPageFooterId);
    pushHeaderFooterIdIfNew(ids, seen, docStyle.evenPageFooterId);
    pushHeaderFooterIdIfNew(ids, seen, docStyle.defaultFooterId);
    collectSectionStyleFooterIdsFromBody(bodyContent, ids, seen);
    const remaining = Object.keys(footersMap)
        .filter(k => !seen.has(k))
        .sort();
    for (const k of remaining) {
        pushHeaderFooterIdIfNew(ids, seen, k);
    }
    return ids;
}

function getStructuralContent(headerOrFooter: unknown): unknown[] | undefined {
    if (!headerOrFooter || typeof headerOrFooter !== 'object') return undefined;
    const c = (headerOrFooter as Record<string, unknown>).content;
    return Array.isArray(c) ? c : undefined;
}

function withSyntheticBodyContent(normalized: GoogleDocForHast, content: unknown[]): GoogleDocForHast {
    return {
        ...normalized,
        body: {
            ...(normalized.body as object),
            content,
        },
    } as GoogleDocForHast;
}

function buildGlobalInlineRefs(
    normalized: GoogleDocForHast,
    headersMap: Record<string, unknown>,
    headerIdsOrdered: string[],
    footersMap: Record<string, unknown>,
    footerIdsOrdered: string[]
): Set<string> {
    const ids = new Set<string>();
    const add = (root: unknown): void => {
        for (const id of collectReferencedInlineObjectIds(root)) ids.add(id);
    };
    add(normalized.body);
    for (const hid of headerIdsOrdered) {
        const content = getStructuralContent(headersMap[hid]);
        if (content) add(content);
    }
    for (const fid of footerIdsOrdered) {
        const content = getStructuralContent(footersMap[fid]);
        if (content) add(content);
    }
    return ids;
}

/**
 * Run `toHast` + missed-image pass for a slice of structural content (header/footer body) using the same
 * `inlineObjects` / `positionedObjects` maps as the main document.
 */
function hastFromStructuralSection(
    raw: Record<string, unknown>,
    tabId: string | null,
    normalized: GoogleDocForHast,
    content: unknown[],
    globalInlineRefs: Set<string>
): Root {
    const sectional = withSyntheticBodyContent(normalized, content);
    const tree = toHast(sectional) as Root;
    insertHastImagesMissedByGoogleDocsHast(raw, tabId, sectional, tree, globalInlineRefs);
    return tree;
}

function imageUrlFromEmbeddedObject(embedded: Record<string, unknown> | undefined): string | undefined {
    if (!embedded) return undefined;
    const img = embedded.imageProperties as Record<string, unknown> | undefined;
    if (!img) return undefined;
    const c = img.contentUri;
    const s = img.sourceUri;
    if (typeof c === 'string' && c.length > 0) return c;
    if (typeof s === 'string' && s.length > 0) return s;
    return undefined;
}

function imageUrlFromInlineObjectEntry(entry: unknown): string | undefined {
    if (!entry || typeof entry !== 'object') return undefined;
    const o = entry as Record<string, unknown>;
    const iop = o.inlineObjectProperties as Record<string, unknown> | undefined;
    const embedded = iop?.embeddedObject as Record<string, unknown> | undefined;
    return imageUrlFromEmbeddedObject(embedded);
}

function imageUrlFromPositionedObjectEntry(entry: unknown): string | undefined {
    if (!entry || typeof entry !== 'object') return undefined;
    const o = entry as Record<string, unknown>;
    const pop = o.positionedObjectProperties as Record<string, unknown> | undefined;
    const embedded = pop?.embeddedObject as Record<string, unknown> | undefined;
    return imageUrlFromEmbeddedObject(embedded);
}

function imageParagraphWithImg(src: string): Element {
    const img: Element = {
        type: 'element',
        tagName: 'img',
        properties: { src },
        children: [],
    };
    return {
        type: 'element',
        tagName: 'p',
        properties: {},
        children: [img],
    };
}

/**
 * How many top-level `tree.children` nodes `toHast` produces after processing the first N
 * `body.content` structural elements (same as google-docs-hast's internal `transform` reduce;
 * `replaceHeaderIdsWithSlug` does not change root child count).
 */
function computeToHastPrefixLengths(normalized: GoogleDocForHast): number[] {
    const bodyContent = (normalized.body as { content?: unknown[] } | undefined)?.content;
    if (!Array.isArray(bodyContent) || bodyContent.length === 0) {
        return [0];
    }
    const n = bodyContent.length;
    const lens: number[] = new Array(n + 1);
    lens[0] = 0;
    for (let i = 0; i < n; i++) {
        const partialDoc = {
            ...normalized,
            body: {
                ...(normalized.body as object),
                content: bodyContent.slice(0, i + 1),
            },
        } as GoogleDocForHast;
        const tree = toHast(partialDoc) as Root;
        lens[i + 1] = tree.children.length;
    }
    return lens;
}

/**
 * `google-docs-hast` only emits paragraph/table structural elements; it skips `positionedObject`
 * (floating images) and may miss inline images when the body references them only indirectly.
 * Insert those images in **document order** (same sequence as `body.content`), not at the end.
 */
function insertHastImagesMissedByGoogleDocsHast(
    raw: Record<string, unknown>,
    tabId: string | null,
    normalized: GoogleDocForHast,
    tree: Root,
    /** When set, only treat inline objects as “orphans” if not referenced anywhere in the full doc (body + headers + footers). */
    globalInlineRefsForOrphans?: Set<string>
): number {
    const bodyContent = (normalized.body as { content?: unknown[] } | undefined)?.content;
    if (!Array.isArray(bodyContent) || bodyContent.length === 0) {
        return 0;
    }

    const tabRef = getTabDocumentRecordFromRaw(raw, tabId);
    const positionedMap = mergedPositionedObjects(raw, tabRef);
    const inlineMap = (normalized.inlineObjects ?? {}) as Record<string, unknown>;
    const inlineRefs = globalInlineRefsForOrphans ?? new Set(collectReferencedInlineObjectIds(normalized.body));
    const seenUrls = new Set<string>();
    let inserted = 0;

    const prefixLens = computeToHastPrefixLengths(normalized);
    const base = tree.children;
    const newChildren: Content[] = [];
    let bi = 0;

    for (let i = 0; i < bodyContent.length; i++) {
        const row = bodyContent[i] as Record<string, unknown>;
        const po = row.positionedObject;
        if (po && typeof po === 'object' && po !== null) {
            const objectId = (po as Record<string, unknown>).objectId;
            if (typeof objectId === 'string') {
                const url = imageUrlFromPositionedObjectEntry(positionedMap[objectId]);
                if (url && !seenUrls.has(url)) {
                    seenUrls.add(url);
                    newChildren.push(imageParagraphWithImg(url));
                    inserted += 1;
                }
            }
        }
        const pid = row.positionedObjectId;
        if (typeof pid === 'string') {
            const url = imageUrlFromPositionedObjectEntry(positionedMap[pid]);
            if (url && !seenUrls.has(url)) {
                seenUrls.add(url);
                newChildren.push(imageParagraphWithImg(url));
                inserted += 1;
            }
        }

        const delta = prefixLens[i + 1] - prefixLens[i];
        for (let k = 0; k < delta; k++) {
            if (bi < base.length) {
                newChildren.push(base[bi]);
                bi++;
            }
        }
    }

    for (const id of Object.keys(inlineMap)) {
        if (inlineRefs.has(id)) continue;
        const url = imageUrlFromInlineObjectEntry(inlineMap[id]);
        if (url && !seenUrls.has(url)) {
            seenUrls.add(url);
            newChildren.push(imageParagraphWithImg(url));
            inserted += 1;
        }
    }

    tree.children = newChildren as Root['children'];

    if (isGoogleDocsHtmlDebugEnabled() && bi !== base.length) {
        debugGoogleDocsHtml('warning: merge length mismatch', { consumedFromBase: bi, baseChildren: base.length });
    }

    return inserted;
}

/**
 * {@link toHast} resolves images via `doc.inlineObjects[id]`. In multi-tab documents, Google often
 * stores `inlineObjects` (with `contentUri`) on each **documentTab**, not at the document root.
 * Merge root + active tab so lookups succeed.
 */
function mergedInlineObjects(
    doc: Record<string, unknown>,
    documentTab: Record<string, unknown> | null | undefined
): GoogleDocForHast['inlineObjects'] {
    const root = (doc.inlineObjects ?? {}) as Record<string, unknown>;
    const tab = (documentTab?.inlineObjects ?? {}) as Record<string, unknown>;
    return { ...root, ...tab } as GoogleDocForHast['inlineObjects'];
}

/**
 * Build a document object with `body` at the root for {@link toHast} (tabbed docs use `documentTab.body`).
 */
export function normalizeGoogleDocForToHast(raw: unknown, tabId: string | null): GoogleDocForHast {
    const doc = raw as Record<string, unknown>;

    if (doc.tab && typeof doc.tab === 'object') {
        const tabObj = doc.tab as TabLike;
        const dt = tabObj.documentTab;
        if (!dt?.body) {
            throw new Error('Document tab has no body');
        }
        return {
            documentId: doc.documentId as string,
            title: doc.title as string | undefined,
            revisionId: doc.revisionId as string | undefined,
            body: dt.body,
            lists: (dt.lists ?? doc.lists ?? {}) as GoogleDocForHast['lists'],
            namedStyles: (dt.namedStyles ?? doc.namedStyles) as GoogleDocForHast['namedStyles'],
            inlineObjects: mergedInlineObjects(doc, dt as Record<string, unknown>),
        };
    }

    const tabs = doc.tabs as TabLike[] | undefined;
    if (tabs && Array.isArray(tabs) && tabs.length > 0) {
        let selected: TabLike | undefined;
        if (tabId) {
            selected = tabs.find(t => t.tabProperties?.tabId === tabId);
            if (!selected) {
                throw new Error(`Tab "${tabId}" not found in document`);
            }
        } else {
            selected = tabs[0];
        }
        const dt = selected.documentTab;
        if (!dt?.body) {
            throw new Error('Selected tab has no document body');
        }
        return {
            documentId: doc.documentId as string,
            title: doc.title as string | undefined,
            revisionId: doc.revisionId as string | undefined,
            body: dt.body,
            lists: (dt.lists ?? doc.lists ?? {}) as GoogleDocForHast['lists'],
            namedStyles: (dt.namedStyles ?? doc.namedStyles) as GoogleDocForHast['namedStyles'],
            inlineObjects: mergedInlineObjects(doc, dt as Record<string, unknown>),
        };
    }

    if (doc.body) {
        return doc as GoogleDocForHast;
    }

    throw new Error('Unrecognized Google Docs JSON shape');
}

export function googleDocJsonToSanitizedHtml(jsonText: string, tabId: string | null): string {
    const raw = JSON.parse(jsonText) as Record<string, unknown>;
    const normalized = normalizeGoogleDocForToHast(raw, tabId);
    const tabRef = getTabDocumentRecordFromRaw(raw, tabId);
    const bodyContent = (normalized.body as { content?: unknown[] } | undefined)?.content ?? [];
    const headersMap = mergedHeadersMap(raw, tabRef);
    const footersMap = mergedFootersMap(raw, tabRef);
    const headerIdsOrdered = collectOrderedHeaderIds(raw, tabRef, headersMap, bodyContent);
    const footerIdsOrdered = collectOrderedFooterIds(raw, tabRef, footersMap, bodyContent);
    const globalInlineRefs = buildGlobalInlineRefs(normalized, headersMap, headerIdsOrdered, footersMap, footerIdsOrdered);

    if (isGoogleDocsHtmlDebugEnabled()) {
        const mergedKeys = Object.keys(normalized.inlineObjects ?? {});
        const posMap = mergedPositionedObjects(raw, tabRef);
        const refsInBody = collectReferencedInlineObjectIds(normalized.body);
        const missingInMap = refsInBody.filter(id => !mergedKeys.includes(id));
        debugGoogleDocsHtml('normalized doc', {
            tabId,
            inlineObjectKeys: mergedKeys.length,
            positionedObjectKeys: Object.keys(posMap).length,
            inlineObjectIdsReferencedInBody: refsInBody.length,
            referencedIdsMissingFromInlineObjects: missingInMap.length ? missingInMap : undefined,
            headerIdsOrdered: headerIdsOrdered.length ? headerIdsOrdered : undefined,
            footerIdsOrdered: footerIdsOrdered.length ? footerIdsOrdered : undefined,
        });
        debugGoogleDocsHtml('inlineObjects sample (first entries)', summarizeInlineObjectsForDebug(normalized.inlineObjects));
    }

    const prefixChildren: Content[] = [];
    for (const hid of headerIdsOrdered) {
        const hc = getStructuralContent(headersMap[hid]);
        if (!hc || hc.length === 0) continue;
        const part = hastFromStructuralSection(raw, tabId, normalized, hc, globalInlineRefs);
        prefixChildren.push(...part.children);
    }

    const tree = toHast(normalized) as Root;
    const appended = insertHastImagesMissedByGoogleDocsHast(raw, tabId, normalized, tree, globalInlineRefs);
    if (isGoogleDocsHtmlDebugEnabled() && appended > 0) {
        debugGoogleDocsHtml('insertHastImagesMissedByGoogleDocsHast', { insertedParagraphs: appended });
    }

    const suffixChildren: Content[] = [];
    for (const fid of footerIdsOrdered) {
        const fc = getStructuralContent(footersMap[fid]);
        if (!fc || fc.length === 0) continue;
        const part = hastFromStructuralSection(raw, tabId, normalized, fc, globalInlineRefs);
        suffixChildren.push(...part.children);
    }

    tree.children = [...prefixChildren, ...tree.children, ...suffixChildren] as Root['children'];
    applyGoogleDocImgReferrerPolicy(tree);

    if (isGoogleDocsHtmlDebugEnabled()) {
        const nImg = countImgNodes(tree);
        debugGoogleDocsHtml('after toHast + missed-image pass + referrerPolicy', {
            imgTagCount: nImg,
            imgSrcSamples: collectImgSrcSamples(tree, 6),
        });
        if (nImg === 0 && Object.keys(normalized.inlineObjects ?? {}).length > 0) {
            debugGoogleDocsHtml(
                'hint: no <img> yet — check embeddedObject.imageProperties (contentUri/sourceUri), or non-image embeds (drawings/charts)'
            );
        }
    }

    const clean = sanitize(tree, googleDocsSanitizeSchema) as Root;

    if (isGoogleDocsHtmlDebugEnabled()) {
        const nImgAfter = countImgNodes(clean);
        const nImgBeforeSanitize = countImgNodes(tree);
        debugGoogleDocsHtml('after sanitize', {
            imgTagCount: nImgAfter,
            imgSrcSamples: collectImgSrcSamples(clean, 6),
        });
        if (nImgBeforeSanitize > 0 && nImgAfter === 0) {
            debugGoogleDocsHtml('warning: sanitize removed all <img> nodes (check src URL protocols)');
        }
    }

    const htmlOut = toHtml(clean);
    logDocHtmlImageSrcsForNetworkDebug(htmlOut);
    return htmlOut;
}

/**
 * Parse `<img src="...">` from serialized doc HTML (for debugging missing network requests).
 */
export function extractImgSrcsFromHtmlString(html: string): string[] {
    const srcs: string[] = [];
    const re = /<img\b[^>]*\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(html)) !== null) {
        const src = m[1] ?? m[2] ?? m[3];
        if (src) srcs.push(src);
    }
    return srcs;
}

/**
 * Uses `console.log` (not `console.debug`) so URLs always show in DevTools.
 * In production, only runs when `localStorage.DEBUG_GOOGLE_DOCS_HTML === '1'`.
 */
export function logDocHtmlImageSrcsForNetworkDebug(html: string): void {
    const dev = process.env.NODE_ENV === 'development';
    const forced = typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_GOOGLE_DOCS_HTML') === '1';
    if (!dev && !forced) return;

    const srcs = extractImgSrcsFromHtmlString(html);
    if (srcs.length === 0) {
        console.log('[googleDocsHtml] no <img src> in final HTML — browser will not request images');
        return;
    }
    console.log(`[googleDocsHtml] ${srcs.length} image URL(s) the browser should request (Network: filter Img, or open in new tab):`, srcs);
}
